import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, FileText, CheckCircle, Clock, DollarSign, Percent,
  ShieldCheck, AlertTriangle, ArrowRight, Lock, Unlock
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { formatPrepaymentSummary } from "../shared/PrepaymentPenaltyInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  submitted: { label: "Awaiting Selection", color: "bg-blue-100 text-blue-700 border-blue-200" },
  borrower_selected: { label: "Awaiting Imaani Capital Approval", color: "bg-amber-100 text-amber-700 border-amber-200" },
  selected: { label: "Approved & Connected", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  passed: { label: "Declined", color: "bg-slate-100 text-slate-500 border-slate-200" },
  withdrawn: { label: "Withdrawn", color: "bg-red-100 text-red-600 border-red-200" },
};

export function TermSheetComparison({ leadId, onRefresh }) {
  const { user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const token = localStorage.getItem("ic_token");

  const fetchSheets = useCallback(() => {
    axios.get(`${API}/term-sheets/${leadId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => setSheets(r.data.term_sheets || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [leadId, token]);

  useEffect(() => { fetchSheets(); }, [fetchSheets]);

  const isBorrower = user?.role === "borrower";
  const isAdmin = ["admin", "originations", "lender_relations"].includes(user?.role);
  const isLender = user?.role === "lender";

  // ── Borrower selects with platform terms ──
  const handleBorrowerSelect = async (tsId) => {
    if (!termsAccepted) {
      toast.error("You must accept the platform terms to proceed.");
      return;
    }
    setSelecting(tsId);
    try {
      const res = await axios.post(`${API}/term-sheets/${tsId}/borrower-select`,
        { platform_terms_accepted: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Term sheet selected! Awaiting Imaani Capital approval.");
      setShowTermsModal(null);
      setTermsAccepted(false);
      fetchSheets();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to select term sheet");
    } finally {
      setSelecting(null);
    }
  };

  // ── Admin approves the borrower's selection ──
  const handleAdminApprove = async (tsId) => {
    if (!window.confirm("Approve this lender selection? Both the borrower and lender will be notified, and borrower details will be released to the lender.")) return;
    setSelecting(tsId);
    try {
      const res = await axios.post(`${API}/term-sheets/${tsId}/admin-approve`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Lender selection approved! Both parties notified.");
      fetchSheets();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    } finally {
      setSelecting(null);
    }
  };

  // ── Lender accepts platform terms ──
  const handleLenderAccept = async (tsId) => {
    if (!termsAccepted) {
      toast.error("You must accept the platform terms to proceed.");
      return;
    }
    setSelecting(tsId);
    try {
      const res = await axios.post(`${API}/term-sheets/${tsId}/lender-accept`,
        { platform_terms_accepted: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Connection is now active!");
      setShowTermsModal(null);
      setTermsAccepted(false);
      fetchSheets();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept");
    } finally {
      setSelecting(null);
    }
  };

  // ── Admin rejects the borrower's lender selection ──
  const handleAdminReject = async (ts) => {
    const reason = window.prompt("Reason for rejection (optional):", "");
    if (reason === null) return; // User cancelled
    const leadId = ts.lead_id;
    setSelecting(ts.ts_id);
    try {
      const res = await axios.post(`${API}/admin/deals/${leadId}/reject-lender-selection`,
        { reason: reason || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Lender selection rejected. Both parties notified.");
      fetchSheets();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    } finally {
      setSelecting(null);
    }
  };

  // ── Legacy/Admin direct select ──
  const handleDirectSelect = async (tsId) => {
    if (!window.confirm("Admin: directly select this term sheet? This bypasses the approval flow.")) return;
    setSelecting(tsId);
    try {
      const res = await axios.post(`${API}/term-sheets/${tsId}/select`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Term sheet selected!");
      fetchSheets();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to select");
    } finally {
      setSelecting(null);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#b89d5a]" /></div>;

  if (sheets.length === 0) return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl border border-dashed border-[#e8e0d0] p-8 text-center" data-testid="no-offers">
      <FileText className="h-10 w-10 text-[#ccc] mx-auto mb-3" />
      <p className="text-sm text-[#888] mb-1">No term sheets received yet</p>
      <p className="text-xs text-[#aaa]">Lenders will submit offers as they review your deal.</p>
    </motion.div>
  );

  const activeSheets = sheets.filter(s => !["passed", "withdrawn", "declined"].includes(s.status));
  const bestRate = Math.min(...activeSheets.filter(s => s.interest_rate).map(s => s.interest_rate));
  const bestPoints = Math.min(...activeSheets.filter(s => s.points != null).map(s => Number(s.points)));
  const bestClose = Math.min(...activeSheets.filter(s => s.closing_days).map(s => s.closing_days));
  const bestLTV = Math.max(...activeSheets.filter(s => s.ltv).map(s => s.ltv));
  const hasConnected = sheets.some(s => s.status === "selected");
  const hasPendingApproval = sheets.some(s => s.status === "borrower_selected");

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="offers-tab">
      {/* Status Banner */}
      {hasPendingApproval && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center gap-2" data-testid="ts-pending-banner">
          <Clock className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="text-xs text-amber-700 font-medium">
            {isAdmin ? "A borrower has selected a term sheet. Review and approve or reject below."
             : "Awaiting Imaani Capital approval. You'll be notified once the selection is reviewed."}
          </span>
        </div>
      )}
      {hasConnected && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2" data-testid="ts-connected-banner">
          <Unlock className="h-4 w-4 text-emerald-600 shrink-0" />
          <span className="text-xs text-emerald-700 font-medium">
            Lender connection is active. Full details are now visible to both parties.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-[#555]">
          {sheets.length} term sheet{sheets.length > 1 ? "s" : ""} received
        </div>
      </div>

      {/* Comparison Table */}
      <div className="overflow-x-auto rounded-xl border border-[#e8e0d0]">
        <table className="w-full text-sm" data-testid="term-sheet-table">
          <thead>
            <tr className="bg-[#faf7f2] border-b border-[#e8e0d0]">
              {["Lender", "Rate", "Type", "LTV", "Points", "Amount", "Term", "Close", "Status",
                ...((isBorrower || isAdmin || isLender) ? ["Action"] : [])
              ].map(h => (
                <th key={h} className={`py-2.5 px-3 ${h === "Lender" ? "text-left" : "text-center"} text-[9px] uppercase tracking-wider text-[#999] font-bold`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sheets.map((ts) => {
              const isConnected = ts.status === "selected";
              const isPassed = ["passed", "withdrawn", "declined"].includes(ts.status);
              const isBorrowerSelected = ts.status === "borrower_selected";
              const isAdminApproved = ts.status === "admin_approved";
              const statusInfo = STATUS_LABELS[ts.status] || { label: ts.status, color: "bg-slate-100 text-slate-600 border-slate-200" };

              return (
                <tr key={ts.ts_id}
                    className={`border-b border-[#f0ebe0] transition-colors ${
                      isConnected ? "bg-emerald-50/50" : isPassed ? "bg-slate-50 opacity-50" :
                      isBorrowerSelected ? "bg-amber-50/50" : isAdminApproved ? "bg-purple-50/50" : "hover:bg-[#faf7f2]"
                    }`}
                    data-testid={`term-sheet-row-${ts.ts_id}`}>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      {isConnected ? <Unlock className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> :
                        <Lock className="h-3.5 w-3.5 text-[#ccc] shrink-0" />}
                      <span className={`font-bold text-xs ${isConnected ? "text-emerald-700" : isPassed ? "text-[#999]" : "text-[#222]"}`}>
                        {ts.lender_name || "Anonymous Lender"}
                      </span>
                    </div>
                  </td>
                  <td className={`py-3 px-3 text-center font-bold text-xs ${ts.interest_rate === bestRate && !isPassed ? "text-emerald-600" : "text-[#222]"}`}>
                    {ts.interest_rate}%
                    {ts.interest_rate === bestRate && activeSheets.length > 1 && !isPassed && <span className="text-[8px] ml-0.5 text-emerald-600">BEST</span>}
                  </td>
                  <td className="py-3 px-3 text-center text-xs">
                    <span className={`px-1.5 py-0.5 rounded font-bold text-[9px] ${ts.payment_structure === "principal_interest" ? "bg-blue-100 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                      {ts.payment_structure === "principal_interest" ? "P+I" : "IO"}
                    </span>
                  </td>
                  <td className={`py-3 px-3 text-center font-bold text-xs ${ts.ltv === bestLTV && !isPassed ? "text-emerald-600" : "text-[#222]"}`}>
                    {ts.ltv || "—"}%
                  </td>
                  <td className={`py-3 px-3 text-center font-bold text-xs ${Number(ts.points) === bestPoints && !isPassed ? "text-emerald-600" : "text-[#222]"}`}>
                    {ts.points || "—"}
                  </td>
                  <td className="py-3 px-3 text-center font-bold text-xs text-[#b89d5a]">
                    ${ts.loan_amount?.toLocaleString() || "—"}
                  </td>
                  <td className="py-3 px-3 text-center text-xs text-[#444]">{ts.term_months || "—"}mo</td>
                  <td className={`py-3 px-3 text-center font-bold text-xs ${ts.closing_days === bestClose && !isPassed ? "text-blue-600" : "text-[#222]"}`}>
                    {ts.closing_days || "—"}d
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[8px] font-bold border ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-center">
                    {/* Borrower: Select (only for submitted sheets, no selection yet) */}
                    {isBorrower && ts.status === "submitted" && !hasConnected && !hasPendingApproval && (
                      <button onClick={() => { setShowTermsModal({ tsId: ts.ts_id, role: "borrower" }); setTermsAccepted(false); }}
                        disabled={selecting === ts.ts_id}
                        className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50 whitespace-nowrap"
                        data-testid={`select-btn-${ts.ts_id}`}>
                        {selecting === ts.ts_id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Select"}
                      </button>
                    )}
                    {/* Admin: Approve (for borrower_selected sheets) */}
                    {isAdmin && ts.status === "borrower_selected" && (
                      <div className="flex items-center gap-1.5 justify-center">
                        <button onClick={() => handleAdminApprove(ts.ts_id)}
                          disabled={selecting === ts.ts_id}
                          className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50 whitespace-nowrap"
                          data-testid={`approve-btn-${ts.ts_id}`}>
                          {selecting === ts.ts_id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Approve"}
                        </button>
                        <button onClick={() => handleAdminReject(ts)}
                          disabled={selecting === ts.ts_id}
                          className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 whitespace-nowrap"
                          data-testid={`reject-btn-${ts.ts_id}`}>
                          Reject
                        </button>
                      </div>
                    )}
                    {/* Borrower/Lender: Awaiting Imaani Capital approval label */}
                    {(isBorrower || isLender) && ts.status === "borrower_selected" && (
                      <span className="text-[9px] text-amber-600 font-semibold">Awaiting Imaani Capital approval</span>
                    )}
                    {/* Admin: Direct select for submitted sheets */}
                    {isAdmin && ts.status === "submitted" && !hasConnected && !hasPendingApproval && (
                      <button onClick={() => handleDirectSelect(ts.ts_id)}
                        disabled={selecting === ts.ts_id}
                        className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-[#333] text-white hover:bg-[#555] disabled:opacity-50 whitespace-nowrap"
                        data-testid={`direct-select-btn-${ts.ts_id}`}>
                        {selecting === ts.ts_id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Direct Select"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detail Cards (show for connected or admin view) */}
      {sheets.filter(s => s.status === "selected" || isAdmin).map(ts => (
        <div key={`detail-${ts.ts_id}`}
             className={`rounded-xl border p-4 ${
               ts.status === "selected" ? "border-emerald-200 bg-emerald-50/30" :
               ts.status === "borrower_selected" ? "border-amber-200 bg-amber-50/30" :
               ["passed", "withdrawn"].includes(ts.status) ? "border-slate-200 bg-slate-50 opacity-50" :
               "border-[#e8e0d0] bg-white"
             }`}
             data-testid={`term-detail-${ts.ts_id}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`font-bold text-sm ${ts.status === "selected" ? "text-emerald-700" : "text-[#222]"}`}>
              {ts.lender_name || "Anonymous Lender"}
            </span>
            <span className="text-[10px] text-[#999]">{ts.created_at ? new Date(ts.created_at).toLocaleDateString() : ""}</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-2">
            <MetricBox label="Rate" value={`${ts.interest_rate}%`} highlight={ts.interest_rate === bestRate} />
            <MetricBox label="Type" value={ts.payment_structure === "principal_interest" ? "P+I" : "IO"} />
            <MetricBox label="LTV" value={`${ts.ltv || "—"}%`} highlight={ts.ltv === bestLTV} />
            <MetricBox label="Points" value={ts.points || "—"} />
            <MetricBox label="Amount" value={`$${ts.loan_amount?.toLocaleString() || "—"}`} />
            <MetricBox label="Term" value={`${ts.term_months || "—"} mo`} />
            <MetricBox label="Close" value={`${ts.closing_days || "—"} days`} highlight={ts.closing_days === bestClose} />
            <MetricBox label="Prepay" value={
              ts.prepayment_penalty_structure
                ? formatPrepaymentSummary(ts.prepayment_penalty_structure, ts.loan_amount, ts.interest_rate)
                : (ts.prepayment_penalty || "None")
            } />
          </div>
          {ts.fees && <div className="text-[10px] text-[#666] mt-2">Fees: {ts.fees}</div>}
          {ts.conditions && <div className="text-[10px] text-amber-600 mt-1">Conditions: {ts.conditions}</div>}
          {ts.notes && <div className="text-[10px] text-[#888] mt-1 italic">{ts.notes}</div>}
        </div>
      ))}

      {/* ── Platform Terms Modal ── */}
      <AnimatePresence>
        {showTermsModal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-[60]" onClick={() => setShowTermsModal(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-[70] flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
                data-testid="platform-terms-modal">
                <div className="p-6 border-b border-[#e8e0d0] bg-[#faf7f2] rounded-t-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="h-5 w-5 text-emerald-700" />
                    <h3 className="text-base font-bold text-[#0A0A0A]">Platform Terms Agreement</h3>
                  </div>
                  <p className="text-xs text-[#888]">
                    {showTermsModal.role === "borrower"
                      ? "Please review and accept the following terms before selecting this term sheet."
                      : "Please review and accept the following terms to complete the lender connection."}
                  </p>
                </div>

                <div className="p-6 space-y-4">
                  <div className="space-y-3 text-xs text-[#555] leading-relaxed">
                    <p className="font-semibold text-[#333]">By accepting these terms, you agree to the following:</p>
                    <ol className="list-decimal pl-5 space-y-2">
                      <li><strong>Platform Exclusivity:</strong> All communication, negotiations, and transactions related to this deal must occur through the Imaani Capital platform. Direct contact outside the platform is prohibited.</li>
                      <li><strong>Fee Structure:</strong> Platform facilitation fees apply as outlined in your user agreement. These fees are due upon successful funding of the deal.</li>
                      <li><strong>Confidentiality:</strong> Deal details, financial information, and counterparty information shared through the platform are confidential and may not be disclosed to third parties.</li>
                      <li><strong>Good Faith:</strong> You agree to negotiate in good faith and provide accurate, timely information throughout the transaction process.</li>
                      <li><strong>Compliance:</strong> You agree to comply with all applicable laws, regulations, and platform policies throughout the transaction.</li>
                    </ol>
                  </div>

                  <div className="flex items-start gap-2 p-3 rounded-xl border border-[#e8e0d0] bg-[#faf7f2]">
                    <input type="checkbox" id="accept-terms" checked={termsAccepted}
                      onChange={e => setTermsAccepted(e.target.checked)}
                      className="mt-0.5 rounded border-[#ccc]"
                      data-testid="accept-terms-checkbox" />
                    <label htmlFor="accept-terms" className="text-xs text-[#333] font-medium cursor-pointer">
                      I have read, understood, and agree to the Imaani Capital Platform Terms & Conditions.
                    </label>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={() => handleBorrowerSelect(showTermsModal.tsId)}
                      disabled={!termsAccepted || selecting}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50"
                      data-testid="confirm-terms-btn">
                      {selecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                      Accept & Select Term Sheet
                    </button>
                    <button onClick={() => { setShowTermsModal(null); setTermsAccepted(false); }}
                      className="px-4 py-2.5 rounded-xl border border-[#e8e0d0] text-xs text-[#666] hover:bg-[#faf7f2] transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function MetricBox({ label, value, highlight = false }) {
  return (
    <div className="text-center">
      <div className="text-[9px] text-[#999] uppercase">{label}</div>
      <div className={`text-xs font-bold ${highlight ? "text-emerald-600" : "text-[#333]"}`}>{value}</div>
    </div>
  );
}
